import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, signal, SimpleChanges } from '@angular/core';
import { FeatureFlagService } from 'feature-flags-lib';
import { IFile } from 'interfaces-lib';
import { map } from 'rxjs';
import { BullhornEntityCollection } from '../../../classes/bullhornEntityCollection';
import { StringUtils } from '../../../classes/stringUtils';
import { BullhornDataService } from '../../../services/bullhorn-data.service';
import { IEntityLabelPair } from '../../../services/bullhorn-meta.service';
import { ConfigService } from '../../../services/config.service';
import { PromptProfileService } from '../../../services/prompt-profile.service';
import { IBullhornEntity } from '../../../types/bullhorn/bullhorn-entity';
import { BullhornEntityType } from '../../../types/bullhorn/bullhorn-entity-type';
import { IJobSubmission } from '../../../types/bullhorn/job-submission';
import { INote } from '../../../types/bullhorn/note';
import { getIPromptOptionsKeys, IPromptOptions } from '../../../types/IPromptProfileMeta';
import { IPromptProfile } from '../../../types/prompt-profile';

@Component({
  selector: 'app-configure-prompt',
  templateUrl: './configure-prompt.component.html',
  styleUrl: './configure-prompt.component.scss',
  standalone: false,
})
export class ConfigurePromptComponent implements OnChanges {
  private static NEW_LEAD: string = 'New Lead';
  private static WEB_RESPONSE_LABEL: string = 'webResponseLabel';

  @Input()
  promptProfile!: IPromptProfile;

  @Input() fileList: IFile[] = [];
  protected filesSelected: IFile[] = [];
  @Input() useFileData = false;

  @Input() noteList: INote[] = [];
  protected notesSelected: INote[] = [];

  @Input() modelList: string[] = [];
  @Input() selectedModel: string | undefined = undefined;
  @Input() promptOptions!: IPromptOptions;
  @Input() selected = {
    role: '',
    tone: '',
    format: '',
    resultLength: '',
    explanationDifficulty: '',
    outputLanguage: '',
  };

  @Input() availableEntities!: BullhornEntityCollection;
  @Input() entities!: IEntityLabelPair[];
  @Input() cardContextEntity!: BullhornEntityType;

  @Output() aiOptions = new EventEmitter<any>();
  @Output() selectedFiles: EventEmitter<{ filesSelected: IFile[] }> = new EventEmitter();
  @Output() selectedNotes: EventEmitter<{ notesSelected: INote[] }> = new EventEmitter();
  @Output() includeEntityData: EventEmitter<{ useEntityData: boolean }> = new EventEmitter();
  fileListTooltip: string | undefined;

  selectedJobSubmission: IJobSubmission[] = [];

  protected readonly BullhornEntityType = BullhornEntityType;

  protected readonly StringUtils = StringUtils;

  private readonly jobSubmissionOnlineResponseFeatureFlag = this.featureFlagService.enabled(
    FeatureFlagService.FLAGS.jobSubmissionOnlineResponse,
  );

  private readonly webResponseLabel = signal<string | undefined>(undefined);

  constructor(
    private promptProfileService: PromptProfileService,
    private bullhornDataService: BullhornDataService,
    protected clientConfigService: ConfigService,
    protected datePipe: DatePipe,
    protected featureFlagService: FeatureFlagService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['noteList']) {
      if (this.promptProfile.noteFilter && this.promptProfile.noteFilter.autoInclude) {
        this.notesSelected = this.noteList;
      }
    }
    if (changes['fileList']) {
      if (this.promptProfile.fileFilter && this.promptProfile.fileFilter.autoInclude) {
        this.filesSelected = this.fileList;
      }
    }
    if (
      !this.webResponseLabel() &&
      this.jobSubmissionOnlineResponseFeatureFlag() &&
      this.isSubmissionTemplateFromJobOrder()
    ) {
      this.bullhornDataService
        .settings(ConfigurePromptComponent.WEB_RESPONSE_LABEL)
        .pipe(map((response) => response.webResponseLabel))
        .subscribe((data) => this.webResponseLabel.set(data));
    }
  }

  private isSubmissionTemplateFromJobOrder(): boolean {
    return (
      this.promptProfile.entityType === BullhornEntityType.JOB_SUBMISSION &&
      this.cardContextEntity === BullhornEntityType.JOB_ORDER
    );
  }

  clearSelections(): void {
    this.filesSelected = [];
    this.notesSelected = [];
  }

  protected sendAiOptions(): void {
    const promptRequest = this.promptProfileService.createPromptRequest(this.promptProfile);
    getIPromptOptionsKeys().forEach((key) => {
      promptRequest[key] = this.selected[key];
    });
    const aiOptions = {
      promptProfile: promptRequest,
      selectedModel: this.selectedModel,
      jobSubmission: this.selectedJobSubmission,
    };
    this.aiOptions.emit(aiOptions);
  }

  protected buildFileListTooltip(): void {
    this.fileListTooltip = this.filesSelected.map((file) => file.name).join(', ');
    this.setFile(this.useFileData); // Re-check the checkbox state
    this.selectedFiles.emit({ filesSelected: this.filesSelected });
  }

  protected setNote(): void {
    this.selectedNotes.emit({ notesSelected: this.notesSelected });
  }

  protected setFile(event: boolean) {
    if (this.filesSelected.length === 0) {
      this.useFileData = false;
    } else {
      this.useFileData = event;
    }
    this.includeEntityData.emit({ useEntityData: this.useFileData });
  }

  // TODO : This method is copied in multiple classes. Should be implemented in a static class.
  public getEntityLabel(entityType: BullhornEntityType): string {
    const entity = this.entities.find((entity) => entity.objectName === entityType);
    return entity ? entity.label : entityType;
  }

  protected updatedSelectedModel(event: any) {
    this.selectedModel = event.value;
  }

  protected updatePromptOption(event: any, key: keyof IPromptOptions) {
    this.selected[key] = event;
  }

  jobSubmissionSelected(selectedJobSubmission: IBullhornEntity) {
    this.selectedJobSubmission.length = 0;
    this.selectedJobSubmission.push(<IJobSubmission>selectedJobSubmission);
  }

  jobSubmissionMultiSelected($event: any) {
    if ($event.entitiesSelected) {
      this.selectedJobSubmission = $event.entitiesSelected as IJobSubmission[];
    }
  }

  protected convertDateFromUnix(timestamp: number | undefined) {
    if (timestamp) {
      return this.datePipe.transform(timestamp, 'medium');
    } else {
      return '';
    }
  }

  get jobSubmissionsLoading(): boolean {
    return (
      this.jobSubmissionOnlineResponseFeatureFlag() &&
      this.isSubmissionTemplateFromJobOrder() &&
      this.webResponseLabel() === undefined
    );
  }

  get availableJobSubmissions() {
    if (
      !this.jobSubmissionOnlineResponseFeatureFlag() ||
      !this.isSubmissionTemplateFromJobOrder()
    ) {
      return this.availableEntities.jobSubmissions;
    }

    const webResponseLabel = this.webResponseLabel();

    return this.availableEntities.jobSubmissions.map((submission) => {
      if (submission.status === ConfigurePromptComponent.NEW_LEAD && webResponseLabel) {
        submission.status = webResponseLabel;
      }
      return submission;
    }) as IJobSubmission[];
  }
}
